<script setup lang="ts">
// Panel 4: Features grouped by how many kept treatments they took this season,
// and how many of them still had production recorded somewhere in the season.
// A click puts that bucket's Feature ids into the spec, so the rest of the
// question re-reads under exactly the Features the bucket counted; while one
// bucket is selected the others are disabled, because the payload has already
// been narrowed to the selected bucket's membership.
import { computed } from "vue";

import { useStoreMapReview } from "@/store/map-review";
import type { MapReviewWasteBucket, MapReviewWasteView } from "@/type/api";

import ReportPanel from "../report/ReportPanel.vue";
import { formatCount, wasteShare } from "./wasteTheme";

const props = defineProps<{
	view: MapReviewWasteView;
}>();

const store = useStoreMapReview();

const selected = computed(() => store.wasteBucket);

const rows = computed(() =>
	props.view.retreatment.map((bucket) => ({
		...bucket,
		share: wasteShare(bucket.stillProducing, bucket.features),
	})),
);

const cap = computed(() => props.view.retreatmentCap.toLocaleString());

/** The title has to say both what a click does now and why a disabled row
 * cannot act: the bucket's Features are no longer in the narrowed payload. */
function bucketTitle(bucket: MapReviewWasteBucket): string {
	if (selected.value === bucket.key) {
		return "Click again to clear this re-treatment bucket";
	}
	if (selected.value !== null) {
		return "Clear the current re-treatment bucket to select this one";
	}
	if (bucket.featureIds.length === 0) {
		return "This bucket carries no selectable Feature ids";
	}
	const count = formatCount(bucket.featureIds.length);
	return bucket.truncated
		? `Select the first ${count} Features in this bucket (the id list is capped)`
		: `Select the ${count} Features in this bucket`;
}
</script>

<template>
	<section class="waste-retreat">
		<ReportPanel title="Re-treating places that keep producing">
			<p class="panel-help">
				Features grouped by how many kept treatments they received this season,
				and how many of those Features still had production recorded somewhere
				in the season. The Features cell selects the bucket, so the whole
				question re-reads under those Features.
			</p>

			<p v-if="rows.length === 0" class="panel-empty">
				No Feature carries a kept treatment in the current selection.
			</p>
			<table v-else class="retreat-table">
				<thead>
					<tr>
						<th>treatments in the season</th>
						<th class="num">features</th>
						<th class="num">still producing</th>
						<th class="num share">share still producing</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="bucket in rows"
						:key="bucket.key"
						:title="bucketTitle(bucket)"
						:class="{
							selected: selected === bucket.key,
							muted: selected !== null && selected !== bucket.key,
						}"
					>
						<td>
							{{ bucket.label }}
							<span
								v-if="bucket.truncated"
								class="cap-mark"
								:title="`The Feature id list stops at ${cap} for this bucket`"
								>capped</span
							>
						</td>
						<td class="num">
							<button
								type="button"
								class="link bucket-button"
								:disabled="
									(bucket.featureIds.length === 0 && selected !== bucket.key) ||
									(selected !== null && selected !== bucket.key)
								"
								:aria-pressed="selected === bucket.key"
								:title="bucketTitle(bucket)"
								@click="store.selectWasteBucket(bucket)"
							>
								{{ formatCount(bucket.features) }}
							</button>
						</td>
						<td class="num">{{ formatCount(bucket.stillProducing) }}</td>
						<td class="num share">
							<div class="share-bar">
								<div
									class="share-fill"
									:style="{
										width: `${bucket.share}%`,
										background: bucket.share > 40 ? '#d55e00' : '#0072b2',
									}"
								/>
							</div>
							<div class="share-value">{{ bucket.share.toFixed(1) }}%</div>
						</td>
					</tr>
				</tbody>
			</table>

			<p class="note">
				Still producing means a live inspection found production at the Feature
				somewhere in the season; production may predate the season's first
				treatment, so this is the re-treatment burden, not a failure rate. A
				truncated bucket selects only the first
				{{ cap }} Feature ids.
			</p>

			<template #help>
				<p>
					One bucket is one treatment count: Features that took exactly one kept
					treatment this season, exactly two, exactly three, four to five, or
					six and more. The still-producing column counts the Features in that
					bucket with a live inspection that recorded production at any point in
					the season.
				</p>
				<p>
					This is a burden, not a rate. A Feature may have produced before its
					first treatment, and the measure does not claim a return inside a
					residual window; no product or intervention in this workspace carries
					a residual duration. Selecting a bucket narrows the shared spec to its
					Feature ids and the other buckets go quiet because their members are
					no longer in the narrowed payload. Clicking the selected bucket again
					clears the selection.
				</p>
			</template>
		</ReportPanel>
	</section>
</template>

<style scoped>
.waste-retreat {
	grid-column: 1 / -1;
	min-width: 0;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.retreat-table {
	width: 100%;
	table-layout: fixed;
	border-collapse: collapse;
	font-size: 0.7rem;
}

/* The four columns divide by rule, so the longest header or the widest count
   cannot push the table past the rail. The label column takes the room; the
   count columns are wide enough for their header words to wrap between words
   rather than inside one, and the share column keeps enough for the bar to
   mean something. */
.retreat-table thead th:nth-child(1) {
	width: 26%;
}

.retreat-table thead th:nth-child(2) {
	width: 20%;
}

.retreat-table thead th:nth-child(3) {
	width: 22%;
}

.retreat-table thead th:nth-child(4) {
	width: 30%;
}

.retreat-table th,
.retreat-table td {
	padding: 4px 6px;
	border-bottom: 1px solid #eef0f2;
	text-align: left;
	vertical-align: middle;
}

.retreat-table th {
	color: #6c757d;
	font-size: 0.64rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

.retreat-table tbody tr:last-child td {
	border-bottom: 0;
}

.retreat-table .num {
	text-align: right;
	font-variant-numeric: tabular-nums;
	/* Numeric cells shrink with their column instead of setting a floor the
	   rail would have to scroll to reach. */
	overflow-wrap: anywhere;
}

/* The selected row is the bucket narrowing the whole report, so it takes the
   same pale-blue tint the ledger's selected rows use. The others are muted
   because their Features are not in the narrowed payload. */
.retreat-table tr.selected td {
	background: #e7f1ff;
}

.retreat-table tr.muted td {
	color: #adb5bd;
}

.link {
	padding: 0;
	border: 0;
	background: none;
	color: #0d6efd;
	font: inherit;
	font-variant-numeric: tabular-nums;
	cursor: pointer;
}

.link:hover {
	text-decoration: underline;
}

.link:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: 2px;
}

/* The selected bucket's button is the clear control, so it stays live while
   the other rows are inert. */
.bucket-button:disabled {
	color: #adb5bd;
	cursor: default;
	text-decoration: none;
}

.cap-mark {
	display: inline-block;
	margin-left: 6px;
	padding: 0 5px;
	border: 1px solid #ced4da;
	border-radius: 9px;
	color: #6c757d;
	font-size: 0.6rem;
	line-height: 1.4;
	vertical-align: 1px;
}

/* The share cell is a bar plus its figure; the column's 30% sets its width, so
   no fixed floor is needed. A 110px floor pushed the four columns past the
   rail and made the whole table scroll. */
.share {
	min-width: 0;
}

.share-bar {
	height: 7px;
	margin-bottom: 2px;
	border-radius: 3px;
	background: #eef0f2;
	overflow: hidden;
}

.share-fill {
	height: 7px;
	border-radius: 3px;
}

.share-value {
	font-size: 0.64rem;
	color: #495057;
}

.retreat-table tr.muted .share-value {
	color: #adb5bd;
}

.note {
	margin: 8px 0 0;
	font-size: 0.66rem;
	line-height: 1.35;
	color: #6c757d;
}
</style>
